import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import LanguageLink from '../components/LanguageLink';
import {
  ArrowRight, 
  Sparkles, 
  Shield, 
  Droplets, 
  AlertTriangle, 
  Sun, 
  TrendingUp,
  Award,
  MapPin,
  Users,
  CheckCircle,
  Star,
  Phone,
  MessageCircle,
  Home,
  Waves,
  Car,
  MoveVertical,
  Building2,
  Footprints,
  ClipboardCheck,
  Wrench
} from 'lucide-react';
import { heroSection, benefits, whyChooseUs, services, contactInfo } from '../data/mock';
import { staticTestimonials } from '../data/staticContent';

const iconMap = {
  Sparkles, Shield, Droplets, AlertTriangle, Sun, TrendingUp,
  Award, MapPin, Users, CheckCircle,
  Home, Waves, Car, MoveVertical, Building2, Footprints
};

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true, margin: "-100px" }
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

const HomePage = () => {
  const { t } = useTranslation();
  const location = useLocation();

  // Helper functions to get translations
  const getServiceTranslations = (serviceId) => {
    const keyMap = {
      1: 'terraces',
      2: 'poolSurrounds',
      3: 'driveways',
      4: 'stairs',
      5: 'balconies',
      6: 'walkways',
      7: 'garages'
    };
    const key = keyMap[serviceId];
    return {
      title: t(`mockData.services.${key}.title`),
      description: t(`mockData.services.${key}.description`),
      benefits: [
        t(`mockData.services.${key}.benefit1`),
        t(`mockData.services.${key}.benefit2`),
        t(`mockData.services.${key}.benefit3`),
        t(`mockData.services.${key}.benefit4`)
      ]
    };
  };

  const getBenefitTranslations = (index) => {
    const keys = ['nonSlip', 'uvResistant', 'lowMaintenance', 'seamlessLook'];
    const key = keys[index];
    return {
      title: t(`mockData.benefits.${key}.title`),
      description: t(`mockData.benefits.${key}.description`)
    };
  };

  const getWhyChooseUsTranslations = (index) => {
    const keys = ['craftsmanship', 'localExpertise', 'customerFocused', 'qualityMaterials'];
    const key = keys[index];
    return {
      title: t(`mockData.whyChooseUs.${key}.title`),
      description: t(`mockData.whyChooseUs.${key}.description`)
    };
  };

  const getTestimonialTranslations = (testimonialId) => {
    const keyMap = {
      1: 'maria',
      2: 'james',
      3: 'carmen'
    };
    const key = keyMap[testimonialId];
    return {
      text: t(`mockData.testimonials.${key}.text`),
      project: t(`mockData.testimonials.${key}.project`)
    };
  };

  useEffect(() => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location]);

  return (
    <>
      <SEO 
        page="home"
      />
      <div className="min-h-screen bg-gradient-to-b from-[#fdfcfb] via-white to-[#f7f5f2]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-28 md:pt-44 md:pb-40 overflow-hidden min-h-[85vh] flex items-center">
        {/* Hero image (LCP element) with responsive srcset — mobile gets 155 KB variant, desktop 440 KB */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/hero-actual-prod.webp"
            srcSet="/images/hero-actual-prod-mobile.webp 1080w, /images/hero-actual-prod.webp 1920w"
            sizes="100vw"
            alt="Stone carpet pool surround Costa del Sol luxury villa modern outdoor living"
            title="Premium stone carpet pool installation Costa del Sol"
            className="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            width="1920"
            height="1080"
          />
          {/* Dark gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/55"></div>
          {/* Subtle animated pattern */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255, 255, 255, 0.2) 1px, transparent 0)',
            backgroundSize: '48px 48px'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div 
            className="max-w-5xl mx-auto text-center"
          >
            <div
              className="inline-flex items-center space-x-2 px-6 py-3 rounded-full bg-white/20 backdrop-blur-md border-2 border-white/50 mb-8 shadow-lg fade-up"
            >
              <Sparkles className="text-[#519dc7]" size={20} />
              <span className="text-sm font-bold text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">{t('home.premierExperts')}</span>
            </div>

            <h1 
              className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-[1.1] fade-up-d1"
              style={{ textShadow: '0 2px 10px rgba(0,0,0,0.4), 0 4px 20px rgba(0,0,0,0.3)' }}
            >
              {t('hero.headline').split(' ').slice(0, 3).join(' ')}
              <br />
              <span className="text-[#519dc7]">
                {t('hero.headline').split(' ').slice(3).join(' ')}
              </span>
            </h1>
            
            <p 
              className="text-xl md:text-2xl text-gray-100 mb-6 leading-relaxed max-w-3xl mx-auto font-medium fade-up-d2"
              style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}
            >
              {t('hero.subheadline')}
            </p>

            {/* Brand Messaging */}
            <div
              className="mb-12 space-y-2 fade-up-d3"
            >
              <p className="text-2xl md:text-3xl font-bold text-white italic drop-shadow-[0_3px_8px_rgba(0,0,0,0.5)]">
                "{t('hero.tagline')}"
              </p>
              <p className="text-lg md:text-xl text-white font-medium italic drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]">
                {t('hero.subtagline')}
              </p>
            </div>

            <div 
              className="flex flex-col sm:flex-row gap-5 justify-center fade-up-d4"
            >
              <LanguageLink to="/contact" onClick={() => setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)}>
                <Button 
                  size="lg" 
                  className="brand-gradient text-white px-12 py-7 text-lg font-bold shadow-2xl hover:shadow-[0_20px_60px_-15px_rgba(81,157,199,0.8)] w-full sm:w-auto group hover:scale-105 transition-all hover:brightness-110"
                >
                  {t('hero.ctaPrimary')}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={22} />
                </Button>
              </LanguageLink>
              <LanguageLink to="/gallery">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="bg-white/15 backdrop-blur-md border-2 border-white/70 text-white hover:bg-white/25 hover:border-white px-12 py-7 text-lg font-bold shadow-xl hover:shadow-2xl w-full sm:w-auto hover:scale-105 transition-all"
                  style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
                >
                  {t('hero.ctaSecondary')}
                </Button>
              </LanguageLink>
            </div>

            {/* Trust badges */}
            <div
              className="flex flex-wrap justify-center gap-10 mt-16 pt-12 border-t border-white/20"
            >
              {[
                { icon: Award, key: 'home.premiumQuality' },
                { icon: Shield, key: 'home.guaranteedWork' },
                { icon: MapPin, key: 'home.localExperts' }
              ].map((item, index) => (
                <div key={index} className="flex items-center space-x-3 text-white">
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-lg">
                    <item.icon size={24} className="text-white drop-shadow-md" />
                  </div>
                  <span className="text-base font-bold drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">{t(item.key)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Decorative bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-20 fill-[#fdfcfb]" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"></path>
          </svg>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-28 bg-white relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="gradient-text">{t('home.whyChooseTitle')}</span>
            </h2>
            <p className="text-xl md:text-2xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
              {t('home.whyChooseSubtitle')}
            </p>
          </div>

          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
          >
            {benefits.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon];
              const translated = getBenefitTranslations(index);
              return (
                <div key={index}>
                  <Card className="elevated-card border-stone-100 h-full hover:border-[#519dc7]/30 group">
                    <CardContent className="p-10">
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#519dc7]/15 to-[#519dc7]/5 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="text-[#519dc7]" size={36} />
                      </div>
                      <h3 className="text-2xl font-semibold text-stone-900 mb-5">
                        {translated.title}
                      </h3>
                      <p className="text-stone-600 leading-relaxed text-lg">
                        {translated.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-28 bg-gradient-to-b from-white to-[#f7f5f2] relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="gradient-text">{t('home.howItWorksTitle')}</span>
            </h2>
            <p className="text-xl md:text-2xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
              {t('home.howItWorksSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
            {/* Step 1 */}
            <div
              className="text-center"
            >
              <Card className="elevated-card border-stone-100 h-full group hover:border-[#519dc7]/30 transition-all">
                <CardContent className="p-10">
                  <div className="relative mb-8">
                    {/* Step Number Badge */}
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-[#519dc7] text-white flex items-center justify-center font-bold text-xl">
                      1
                    </div>
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#519dc7]/15 to-[#519dc7]/5 flex items-center justify-center mx-auto mt-6 group-hover:scale-110 transition-transform duration-300">
                      <MessageCircle className="text-[#519dc7]" size={36} />
                    </div>
                  </div>
                  <h3 className="text-2xl font-semibold text-stone-900 mb-5">
                    {t('home.step1Title')}
                  </h3>
                  <p className="text-stone-600 leading-relaxed text-lg">
                    {t('home.step1Description')}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Step 2 */}
            <div
              className="text-center"
            >
              <Card className="elevated-card border-stone-100 h-full group hover:border-[#519dc7]/30 transition-all">
                <CardContent className="p-10">
                  <div className="relative mb-8">
                    {/* Step Number Badge */}
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-[#519dc7] text-white flex items-center justify-center font-bold text-xl">
                      2
                    </div>
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#519dc7]/15 to-[#519dc7]/5 flex items-center justify-center mx-auto mt-6 group-hover:scale-110 transition-transform duration-300">
                      <Wrench className="text-[#519dc7]" size={36} />
                    </div>
                  </div>
                  <h3 className="text-2xl font-semibold text-stone-900 mb-5">
                    {t('home.step2Title')}
                  </h3>
                  <p className="text-stone-600 leading-relaxed text-lg">
                    {t('home.step2Description')}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Step 3 */}
            <div
              className="text-center"
            >
              <Card className="elevated-card border-stone-100 h-full group hover:border-[#519dc7]/30 transition-all">
                <CardContent className="p-10">
                  <div className="relative mb-8">
                    {/* Step Number Badge */}
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-[#519dc7] text-white flex items-center justify-center font-bold text-xl">
                      3
                    </div>
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#519dc7]/15 to-[#519dc7]/5 flex items-center justify-center mx-auto mt-6 group-hover:scale-110 transition-transform duration-300">
                      <CheckCircle className="text-[#519dc7]" size={36} />
                    </div>
                  </div>
                  <h3 className="text-2xl font-semibold text-stone-900 mb-5">
                    {t('home.step3Title')}
                  </h3>
                  <p className="text-stone-600 leading-relaxed text-lg">
                    {t('home.step3Description')}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA Button */}
          <div
            className="text-center"
          >
            <LanguageLink to="/contact" onClick={() => setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)}>
              <Button className="brand-gradient hover:opacity-90 text-white text-lg px-10 py-6 rounded-full shadow-xl hover:shadow-2xl transition-all">
                {t('home.getFreeQuote')}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </LanguageLink>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-28 bg-gradient-to-b from-[#f7f5f2] to-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#519dc7]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#898989]/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="gradient-text">{t('home.ourServicesTitle')}</span>
            </h2>
            <p className="text-xl md:text-2xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
              {t('home.ourServicesSubtitle')}
            </p>
          </div>

          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
          >
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Home;
              const translated = getServiceTranslations(service.id);
              return (
                <div key={service.id}>
                  <LanguageLink to="/gallery" state={{ category: service.title }}>
                    <Card className="overflow-hidden elevated-card border-stone-100 h-full group cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
                      <div className="relative h-72 overflow-hidden">
                        <img
                          src={service.image}
                          alt={service.alt || `Stone carpet ${translated.title.toLowerCase()} Costa del Sol premium installation`}
                          title={service.titleAttr || `${translated.title} stone carpet Costa del Sol`}
                          width="800"
                          height="600"
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-110 group-hover:brightness-110 transition-all duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/50 to-transparent"></div>
                        <div className="absolute inset-0 bg-[#519dc7]/0 group-hover:bg-[#519dc7]/15 transition-colors duration-500"></div>
                        {/* Additional dark overlay on hover */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300"></div>
                        <div className="absolute bottom-6 left-6 right-6">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <IconComponent className="text-white" size={20} />
                            </div>
                          </div>
                          <h3 className="text-3xl font-bold text-white group-hover:scale-105 transition-transform duration-300">
                            {translated.title}
                          </h3>
                        </div>
                      </div>
                      <CardContent className="p-8">
                        <p className="text-stone-600 mb-6 leading-relaxed text-lg">
                          {translated.description}
                        </p>
                        <ul className="space-y-3">
                          {translated.benefits.slice(0, 3).map((benefit, idx) => (
                            <li key={idx} className="flex items-center text-stone-700">
                              <CheckCircle size={18} className="text-[#519dc7] mr-3 flex-shrink-0" />
                              <span className="font-medium">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </LanguageLink>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-20">
            <LanguageLink to="/services">
              <Button size="lg" className="brand-gradient hover:opacity-90 text-white px-12 py-7 text-lg shadow-xl hover:scale-105 transition-all">
                {t('home.viewAllServices')}
                <ArrowRight className="ml-2" size={22} />
              </Button>
            </LanguageLink>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-28 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="gradient-text">{t('home.whyChooseUsTitle')}</span>
            </h2>
            <p className="text-xl md:text-2xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
              {t('home.whyChooseUsSubtitle')}
            </p>
          </div>

          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10"
          >
            {whyChooseUs.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              const translated = getWhyChooseUsTranslations(index);
              return (
                <div key={index} className="text-center group">
                  <div className="w-24 h-24 rounded-2xl brand-gradient flex items-center justify-center mx-auto mb-8 shadow-lg group-hover:scale-110 group-hover:shadow-2xl transition-all duration-300">
                    <IconComponent className="text-white" size={40} />
                  </div>
                  <h3 className="text-2xl font-semibold text-stone-900 mb-5">
                    {translated.title}
                  </h3>
                  <p className="text-stone-600 leading-relaxed text-lg">
                    {translated.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-28 bg-gradient-to-b from-[#f7f5f2] to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="gradient-text">{t('home.testimonialsTitle')}</span>
            </h2>
            <p className="text-xl md:text-2xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
              {t('home.testimonialsSubtitle')}
            </p>
          </div>

          <div 
            className="grid grid-cols-1 md:grid-cols-3 gap-10"
          >
            {staticTestimonials.map((testimonial) => {
              const translated = getTestimonialTranslations(testimonial.id);
              return (
                <div key={testimonial.id}>
                  <Card className="elevated-card border-stone-100 h-full hover:border-[#519dc7]/30">
                    <CardContent className="p-10">
                      <div className="flex items-center mb-8">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <Star key={i} size={22} className="text-[#519dc7] fill-[#519dc7]" />
                        ))}
                      </div>
                      <p className="text-stone-700 mb-8 leading-relaxed text-lg italic">
                        "{translated.text}"
                      </p>
                      <div className="border-t border-stone-200 pt-6">
                        <p className="font-semibold text-stone-900 text-xl">{testimonial.name}</p>
                        <p className="text-[#519dc7] font-medium mt-3 text-sm uppercase tracking-wide">{translated.project}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 brand-gradient"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzMuMzE0IDAgNiAyLjY4NiA2IDZzLTIuNjg2IDYtNiA2LTYtMi42ODYtNi02IDIuNjg2LTYgNi02ek0yNCAzOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-10">
              {t('home.ctaTitle')}
            </h2>
            <p className="text-xl md:text-2xl text-white/95 mb-14 leading-relaxed">
              {t('home.ctaSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <LanguageLink to="/contact" onClick={() => setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)}>
                <Button size="lg" className="bg-white hover:bg-stone-50 text-[#519dc7] px-12 py-7 text-lg font-semibold shadow-2xl hover:shadow-[0_20px_60px_-15px_rgba(255,255,255,0.6)] w-full sm:w-auto group hover:scale-105 transition-all">
                  {t('home.requestFreeQuote')}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={22} />
                </Button>
              </LanguageLink>
              <a 
                href={`tel:${contactInfo.phone}`}
                className="inline-block"
              >
                <Button size="lg" variant="outline" className="glass border-white/60 text-white hover:bg-white/15 px-12 py-7 text-lg font-semibold backdrop-blur-md w-full sm:w-auto hover:scale-105 transition-all">
                  <Phone className="mr-2" size={22} />
                  {t('home.callUsNow')}
                </Button>
              </a>
              <a
                href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-12 py-7 text-lg font-semibold shadow-xl w-full sm:w-auto hover:scale-105 transition-all">
                  <MessageCircle className="mr-2" size={22} />
                  {t('home.whatsappUs')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default HomePage;
